import classNames from "classnames";
import { isEqual } from "lodash";
import moment from "moment-timezone";
import Form, { List } from "rc-field-form";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/Button";
import Filter from "./Filter";
import Toggle from "./Toggle";
import { useFilterFields } from "../../api";
import { useDateFilters } from "../../../../components/DateRangeFilters/useDateFilters";
import { useVisible } from "../../../../utils/hooks/useVisible";
import "./_style.scss";

export const initialFilters = [{ member: "", operator: "", values: "" }];

export const isFilterValid = ({ member, operator, values }) => {
  if (Array.isArray(values) && values.length === 0) {
    return false;
  }
  return !!member && !!operator && ![undefined, null, ""].includes(values);
};

function Filters({ report, filters, setFilters }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { visible, toggle } = useVisible(false);
  const list = Form.useWatch("filters", form);
  const { mode, payGroup } = useDateFilters();

  const { filterFields, getFilterFields } = useFilterFields();

  const config = useMemo(() => {
    const columnTypes = report?.reportData?.columns?.map((item) =>
      item?.name?.split(".")[0]
    );

    const fields = filterFields?.reduce((total, current) => {
      if (columnTypes?.includes(current?.name)) {
        const fields = current?.fields?.map((field) => ({
          ...field,
          table: current?.name,
          table_display_name: current?.display_name,
          ...(field?.children?.length
            ? { children: field.children.filter((child) => child?.canFilter) }
            : {}),
        }));
        total.push(fields);
      }
      return total.flat();
    }, []);

    return fields?.filter((field) => field?.canFilter || field?.children?.length);
  }, [filterFields, report?.reportData?.columns]);

  const hasChange = useMemo(() => {
    return !isEqual(filters, list);
  }, [filters, list]);

  const areValid = useMemo(() => {
    return list?.every(isFilterValid);
  }, [list]);

  const changeFieldMethod = useCallback(
    (name, operator) => {
      form.setFieldValue(["filters", name, "operator"], operator);
    },
    [form]
  );

  const changeFieldValue = useCallback(
    (name, value) => {
      form.setFieldValue(["filters", name, "values"], value);
    },
    [form]
  );

  const onFinish = useCallback(
    (values) => {
      setFilters(values.filters);
    },
    [setFilters]
  );

  const clearFilters = useCallback(() => {
    let updatedFilters = initialFilters;

    if (mode === "pay-group" && payGroup) {
      const payGroupFilter = list?.find((filter) =>
        (filter?.member || "").toLowerCase().includes("paygroup")
      );

      if (payGroupFilter) {
        updatedFilters = initialFilters.filter(
          (filter) => !(filter?.member || "").toLowerCase().includes("paygroup")
        );
        updatedFilters.push(payGroupFilter);
      }
    }

    updatedFilters = updatedFilters.filter((filter) => !!filter?.member);
  
    if (updatedFilters?.length === 0 || !updatedFilters) {
      updatedFilters = initialFilters;
      setFilters(initialFilters);
      let filterValues = JSON.parse(sessionStorage.getItem("filters")) || {};
      filterValues["report-" + report?.id] = {
        ...filterValues["report-" + report?.id],
        columnFilters: initialFilters
      };
      sessionStorage.setItem("filters", JSON.stringify(filterValues));
    }

    if (!isEqual(filters, updatedFilters)) {
      setFilters(updatedFilters);
    }

    form.setFieldValue("filters", updatedFilters);
  }, [mode, payGroup, filters, form, list, setFilters, report?.id]);

  useEffect(() => {
    const data = filters?.map((filter) => {
      const matchingItem = config?.find((item) => item.name === filter.member);

      const isValidDate = (value) => value instanceof Date || moment.isMoment(value);
      const isValidDateString = (value) => moment(value, "YYYY-MM-DD", true).isValid();

      if (matchingItem) {
        return {
          ...filter,
          values: (
            (!matchingItem?.fieldType || matchingItem?.fieldType === "text") &&
            filter?.values &&
            Array.isArray(filter?.values)
          )
            ? ((isValidDate(filter?.values?.[0]) || isValidDateString(filter?.values?.[0]))
              ? moment(filter?.values?.[0]).toDate()
              : filter?.values)
            : ((isValidDate(filter?.values) || isValidDateString(filter?.values))
              ? moment(filter?.values).toDate()
              : filter?.values),
        }
      } else {
        return {
          ...filter,
          values: (
            filter?.values &&
            Array.isArray(filter?.values)
          )
            ? ((isValidDate(filter?.values?.[0]) || isValidDateString(filter?.values?.[0]))
              ? moment(filter?.values?.[0]).toDate()
              : filter?.values)
            : ((isValidDate(filter?.values) || isValidDateString(filter?.values))
              ? moment(filter?.values).toDate()
              : filter?.values),
        }
      }
    });

    form.setFieldValue("filters", data || initialFilters);
  }, [form, config, filters]);

  useEffect(() => {
    const controller = new AbortController();
    getFilterFields(controller);
    return () => controller.abort();
  }, [getFilterFields]);

  useEffect(() => {
    if (mode === "pay-group" && payGroup) {
      let member = config?.find((item) => (item.name.toLowerCase()).includes("paygroup"));
      member = member ? `${member.table}.${member.name}` : "payGroup";
      const hasExisting = !!filters
        ?.find((filter) => ((filter?.member || "").toLowerCase() || "").includes("paygroup"));
      let newValue;

      if (payGroup?.id) {
        if (hasExisting) {
          newValue = filters.map((filter) => {
            if (!((filter?.member || "").toLowerCase() || "").includes("paygroup")) {
              return filter;
            }
            return {
              ...filter,
              values: [payGroup],
            };
          });
        } else {
          const existingIndex = filters?.findIndex(
            (filter) => filter.values === "" || filter?.values?.length === 0
          );

          if (existingIndex === -1) {
            newValue = [
              {
                operator: "exact",
                member,
                values: [payGroup],
              },
              ...(filters || []),
            ];
          } else {
            newValue = [...(filters || [])];
            newValue.splice(existingIndex, 1, {
              operator: "exact",
              member,
              values: [payGroup],
            });
          }
        }
      } else {
        newValue = filters?.filter((filter) => {
          return !((filter?.member || "").toLowerCase() || "").includes("paygroup");
        });
      }

      if (JSON.stringify(newValue) !== JSON.stringify(filters)) {
        form.setFieldValue("filters", newValue);
      }
    }
  }, [config, filters, form, mode, payGroup]);

  return (
    <Form
      initialValues={{ filters }}
      form={form}
      onFinish={onFinish}
      className="table-filters d-flex align-items-start justify-content-end w-100 my-3"
    >
      <div className={classNames("responsive-filters", visible ? "d-flex flex-column" : "d-none")}>
        <div className="d-flex flex-column gap-y-4 px-0 w-100">
          <List name="filters">
            {(fields, { add, remove }) =>
              fields.map(({ name, key }) => (
                <Filter
                  changeFieldMethod={changeFieldMethod}
                  changeFieldValue={changeFieldValue}
                  config={config}
                  name={name}
                  key={key}
                  form={form}
                  add={() => add({}, name + 1)}
                  remove={() => remove(name)}
                />
              ))
            }
          </List>
        </div>

        <div className="d-flex mt-3">
          <Button
            color="primary"
            size="sm"
            className="mr-2"
            disabled={!hasChange || !areValid}
          >
            {t("search")}
          </Button>
          <Button
            color="secondary"
            size="sm"
            onClick={clearFilters}
            disabled={!hasChange && isEqual(list, initialFilters)}
          >
            {t("clear-filters")}
          </Button>
        </div>
      </div>
      <div className="ml-3">
        <Toggle open={visible} toggle={toggle} />
      </div>
    </Form>
  );
}

export default Filters;
